import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import type { EngagementOpportunity } from '../../store/slices/socialLearningAnalyticsSlice';

interface EngagementOptimizationCardProps {
  opportunity: EngagementOpportunity;
  onImplement: () => void;
}

export const EngagementOptimizationCard: React.FC<
  EngagementOptimizationCardProps
> = ({ opportunity, onImplement }) => {
  const [expanded, setExpanded] = useState(false);
  const [animation] = useState(new Animated.Value(0));

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'peer_interaction':
        return 'people';
      case 'group_participation':
        return 'chatbubbles';
      case 'content_sharing':
        return 'share';
      case 'mentoring':
        return 'school';
      default:
        return 'bulb';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'peer_interaction':
        return '#2196F3';
      case 'group_participation':
        return '#4CAF50';
      case 'content_sharing':
        return '#FF9800';
      case 'mentoring':
        return '#9C27B0';
      default:
        return '#666';
    }
  };

  const getEffortColor = (effort: string) => {
    switch (effort) {
      case 'low':
        return '#4CAF50';
      case 'medium':
        return '#FF9800';
      case 'high':
        return '#F44336';
      default:
        return '#666';
    }
  };

  const getImpactColor = (impact: number) => {
    if (impact >= 0.8) return '#4CAF50';
    if (impact >= 0.6) return '#8BC34A';
    if (impact >= 0.4) return '#FF9800';
    return '#F44336';
  };

  const toggleExpanded = () => {
    const toValue = expanded ? 0 : 1;
    setExpanded(!expanded);

    Animated.timing(animation, {
      toValue,
      duration: 300,
      useNativeDriver: false,
    }).start();
  };

  const expandedHeight = animation.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 120],
  });

  return (
    <View style={styles.container}>
      {/* Header */}
      <TouchableOpacity style={styles.header} onPress={toggleExpanded}>
        <View style={styles.iconContainer}>
          <Ionicons
            name={getTypeIcon(opportunity.type) as any}
            size={20}
            color={getTypeColor(opportunity.type)}
          />
        </View>
        <View style={styles.titleContainer}>
          <Text style={styles.title}>{opportunity.title}</Text>
          <View style={styles.metaInfo}>
            <View style={styles.impactBadge}>
              <Text
                style={[
                  styles.impactText,
                  { color: getImpactColor(opportunity.potentialImpact) },
                ]}
              >
                +{Math.round(opportunity.potentialImpact * 100)}% impact
              </Text>
            </View>
            <View
              style={[
                styles.effortBadge,
                { backgroundColor: getEffortColor(opportunity.effort) + '20' },
              ]}
            >
              <Text
                style={[
                  styles.effortText,
                  { color: getEffortColor(opportunity.effort) },
                ]}
              >
                {opportunity.effort} effort
              </Text>
            </View>
          </View>
        </View>
        <View style={styles.expandButton}>
          <Ionicons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={20}
            color='#666'
          />
        </View>
      </TouchableOpacity>

      {/* Description */}
      <Text style={styles.description}>{opportunity.description}</Text>

      {/* Success Probability */}
      <View style={styles.probabilitySection}>
        <View style={styles.probabilityHeader}>
          <Ionicons name='trending-up' size={16} color='#4CAF50' />
          <Text style={styles.probabilityLabel}>Success Probability</Text>
        </View>
        <View style={styles.probabilityBar}>
          <View
            style={[
              styles.probabilityFill,
              {
                width: `${opportunity.successProbability * 100}%`,
                backgroundColor: getImpactColor(opportunity.successProbability),
              },
            ]}
          />
        </View>
        <Text style={styles.probabilityText}>
          {Math.round(opportunity.successProbability * 100)}% likely to succeed
        </Text>
      </View>

      {/* Timeframe */}
      <View style={styles.timeframeSection}>
        <Ionicons name='time-outline' size={16} color='#666' />
        <Text style={styles.timeframeText}>
          Expected timeframe: {opportunity.timeframe}
        </Text>
      </View>

      {/* Expanded Content */}
      <Animated.View
        style={[styles.expandedContent, { height: expandedHeight }]}
      >
        <View style={styles.aiReasoningSection}>
          <Text style={styles.aiReasoningTitle}>AI Reasoning</Text>
          <Text style={styles.aiReasoningText}>{opportunity.aiReasoning}</Text>
        </View>
      </Animated.View>

      {/* Action Button */}
      <TouchableOpacity
        style={[
          styles.implementButton,
          { backgroundColor: getTypeColor(opportunity.type) },
        ]}
        onPress={onImplement}
      >
        <Ionicons name='play' size={16} color='#fff' />
        <Text style={styles.implementButtonText}>Implement Suggestion</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  iconContainer: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  titleContainer: {
    flex: 1,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  metaInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  impactBadge: {
    backgroundColor: '#e8f5e8',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  impactText: {
    fontSize: 11,
    fontWeight: '600',
  },
  effortBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  effortText: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  expandButton: {
    padding: 4,
  },
  description: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
    marginBottom: 16,
  },
  probabilitySection: {
    marginBottom: 12,
  },
  probabilityHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  probabilityLabel: {
    fontSize: 13,
    color: '#666',
    marginLeft: 6,
  },
  probabilityBar: {
    height: 6,
    backgroundColor: '#f0f0f0',
    borderRadius: 3,
    marginBottom: 4,
  },
  probabilityFill: {
    height: '100%',
    borderRadius: 3,
  },
  probabilityText: {
    fontSize: 12,
    color: '#666',
  },
  timeframeSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  timeframeText: {
    fontSize: 13,
    color: '#666',
    marginLeft: 6,
  },
  expandedContent: {
    overflow: 'hidden',
    marginBottom: 16,
  },
  aiReasoningSection: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
  },
  aiReasoningTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  aiReasoningText: {
    fontSize: 12,
    color: '#666',
    lineHeight: 16,
  },
  implementButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  implementButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
});
